import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../firebase';
import { REVIEW_MODERATOR_UID } from './ratings';

const BLOCKED_NAMES = ['realitycheck', 'admin', 'moderator', 'official'];
const HIDDEN_CHAR = /[\u200B\u200C\u200E\u200F\u202A-\u202E\u2060-\u2064\u2066-\u206F\uFEFF\u180E\u00AD]/;

export function normalizeDisplayName(raw) {
  if (typeof raw !== 'string') return '';
  return raw.trim().replace(/[ \t\u00A0]+/g, ' ').normalize('NFC');
}

function graphemes(text) {
  return Array.from(
    new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(text),
    (part) => part.segment
  );
}

function hasBlockedFormat(text) {
  if (HIDDEN_CHAR.test(text)) return true;
  for (const ch of text) {
    const cp = ch.codePointAt(0);
    if (cp === 0x200D) continue;
    if (cp >= 0xE0020 && cp <= 0xE007F) continue;
    if (/\p{Cf}/u.test(ch)) return true;
  }
  return false;
}

function combiningMarkCount(grapheme) {
  let count = 0;
  for (const ch of grapheme) {
    if (ch === '\uFE0E' || ch === '\uFE0F') continue;
    if (/\p{M}/u.test(ch)) count += 1;
  }
  return count;
}

function isEmojiGrapheme(grapheme) {
  return /\p{Extended_Pictographic}|\p{Regional_Indicator}|\u20E3/u.test(grapheme);
}

function isPlainGrapheme(grapheme) {
  return /^[\p{L}\p{N} .'_-]$/u.test(grapheme) || /^[\p{L}\p{N}][\p{M}]{1,2}$/u.test(grapheme);
}

export function displayNameError(raw, uid) {
  if (typeof raw !== 'string') return 'Enter a display name.';
  const name = normalizeDisplayName(raw);
  if (!name) return 'Enter a display name.';
  if (hasBlockedFormat(name)) return 'That name uses hidden or unsupported characters.';
  const parts = graphemes(name);
  if (parts.length < 2 || parts.length > 40) return 'Display name must be 2–40 characters.';
  let emojis = 0;
  let hasLetterOrNumber = false;
  for (const part of parts) {
    if (combiningMarkCount(part) > 2) return 'That name uses hidden or unsupported characters.';
    if (isEmojiGrapheme(part)) {
      emojis += 1;
      continue;
    }
    if (/\p{L}|\p{N}/u.test(part)) hasLetterOrNumber = true;
    if (!isPlainGrapheme(part)) return "Use only letters, numbers, spaces, . - _ ', and emojis.";
  }
  if (emojis > 3) return 'Use no more than 3 emojis.';
  if (!hasLetterOrNumber) return 'Include at least one letter or number.';
  const lower = name.toLowerCase();
  if (uid !== REVIEW_MODERATOR_UID && BLOCKED_NAMES.some((word) => lower.includes(word))) {
    return "That display name isn't allowed.";
  }
  return '';
}

export function firstGrapheme(text) {
  const value = typeof text === 'string' ? text.trim() : '';
  if (!value) return '';
  const first = graphemes(value)[0] || '';
  return /\p{L}/u.test(first) ? first.toLocaleUpperCase() : first;
}

export async function saveDisplayName(displayName) {
  const functions = getFunctions(app, 'us-central1');
  const callable = httpsCallable(functions, 'updateDisplayName');
  const result = await callable({ displayName });
  return result.data;
}

export function callableErrorMessage(err) {
  const raw = (err && err.message) || 'Could not save your display name.';
  return raw.replace(/^Firebase:\s*/i, '').replace(/\s*\(functions\/[a-z0-9-]+\)\.?$/i, '');
}
